import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ListChecks, Trash2, X } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { displayName, useSocialStore } from '@/store/social'
import { useGuildStore } from '@/store/guilds'
import { applyPinnedDeletion, bulkDeleteApi, MAX_BULK_DELETE } from '@/lib/bulkDelete'
import { applySavedEvent } from '@/lib/savedMessages'
import { applyInboxDeletion } from '@/lib/inbox'
import { describeError } from '@/lib/auth'
import { formatDateTime } from '@/lib/format'
import { Modal } from '../settings/Modal'
import type { useMessageSelection } from './useMessageSelection'
import type { Message } from '@/types/api'
import buttons from '../guild/ConfirmModal.module.css'
import styles from './MessageSelection.module.css'

type Selection = ReturnType<typeof useMessageSelection>

export function MessageSelectionControls({ selection }: { selection: Selection }) {
  return <div className={styles.bar} role="region" aria-label="Выбор сообщений для удаления">
    <span className={styles.count} role="status"><ListChecks size={18} />Выбрано: {selection.chosen.length} / {MAX_BULK_DELETE}</span>
    <div className={styles.actions}>
      <button type="button" className={styles.action} onClick={selection.selectLoaded}>{selection.availableCount > MAX_BULK_DELETE ? `Выбрать последние ${MAX_BULK_DELETE}` : 'Выбрать загруженные'}</button>
      {selection.chosen.length ? <button type="button" className={styles.action} onClick={selection.clear}>Снять выбор</button> : null}
      <button type="button" className={styles.remove} disabled={!selection.chosen.length} onClick={selection.openConfirm}><Trash2 size={16} />Удалить</button>
      <button type="button" className={styles.close} aria-label="Отменить выбор сообщений" data-tooltip="Отмена" onClick={selection.finish}><X size={20} /></button>
    </div>
  </div>
}

/** Снимок выбора: новые сообщения не добавляются в уже открытое подтверждение. */
export function BulkDeleteModal({ channelId, channelName, messages, onCancel, onDeleted }: { channelId: string; channelName: string; messages: Message[]; onCancel: () => void; onDeleted: () => void }) {
  const userId = useAuthStore((s) => s.user?.id)
  const client = useQueryClient()
  const busyRef = useRef(false)
  const [busy, setBusy] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const confirm = async () => {
    if (busyRef.current || !userId) return
    busyRef.current = true; setBusy(true); setError(null)
    try {
      const result = await bulkDeleteApi.remove(channelId, messages.map((m) => m.id), reason)
      if (useAuthStore.getState().user?.id !== userId) return
      useSocialStore.getState().applyEvent('MESSAGE_DELETE_BULK', result)
      useGuildStore.getState().applyEvent('MESSAGE_DELETE_BULK', result)
      applySavedEvent(client, userId, 'MESSAGE_DELETE_BULK', result)
      applyInboxDeletion(client, userId, 'MESSAGE_DELETE_BULK', result)
      applyPinnedDeletion(client, userId, 'MESSAGE_DELETE_BULK', result)
      onDeleted()
    } catch (err) { setError(describeError(err).message) }
    finally { busyRef.current = false; setBusy(false) }
  }
  return <Modal title={`Удалить сообщения — ${messages.length}`} onClose={() => { if (!busyRef.current) onCancel() }} width={520}>
    <p className={buttons.text}>Канал «{channelName}». Сообщения удалятся для всех участников.</p>
    <ol className={`${styles.preview} scroller`} aria-label="Сообщения для удаления">
      {messages.map((m) => <li key={m.id}>
        <div className={styles.previewHead}><strong>{displayName(m.author)}</strong><time dateTime={m.timestamp}>{formatDateTime(new Date(m.timestamp))}</time></div>
        <p>{m.content || m.message_snapshots?.[0]?.message.content || m.poll?.question.text || m.embeds?.[0]?.title || m.attachments?.map((a) => a.filename).join(', ') || (m.sticker_items?.length ? 'Стикер' : 'Сообщение без текста')}</p>
      </li>)}
    </ol>
    <label className={styles.reason}><span>Причина <small>(необязательно)</small></span><input autoFocus maxLength={512} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} /></label>
    {error ? <p className={buttons.error} role="alert">{error}</p> : null}
    <div className={buttons.buttons}>
      <button type="button" className={buttons.cancel} disabled={busy} onClick={onCancel}>Отмена</button>
      <button type="button" className={buttons.confirm} disabled={busy || !messages.length} onClick={() => void confirm()}>{busy ? 'Удаляем…' : `Удалить ${messages.length}`}</button>
    </div>
  </Modal>
}
